import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Select } from "@sagot/ui";
import { useState } from "react";
import type { TemplateOption } from "./ranking";

/** What switching would throw away: the user's edits, an unedited AI draft, or nothing. */
export type ReplaceKind = "edits" | "ai" | null;

interface TemplateSwitcherProps {
  options: readonly TemplateOption[];
  recommended: string | undefined;
  /** The template the editor currently shows. */
  current: string;
  /** The template being loaded, if any (shown selected while the editor is skeleton). */
  loading: string | null;
  replace: ReplaceKind;
  onSwitch: (key: string, name: string) => void;
}

/** "Purpose template" Select; replacing edited fields asks for confirmation first. */
export function TemplateSwitcher({ options, recommended, current, loading, replace, onSwitch }: TemplateSwitcherProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<TemplateOption | null>(null);
  const change = (key: string) => {
    const option = options.find((candidate) => candidate.key === key);
    if (!option || key === current) return;
    if (replace) setPending(option);
    else onSwitch(option.key, option.name);
  };
  const confirm = () => {
    if (pending) onSwitch(pending.key, pending.name);
    setPending(null);
  };
  return (
    <>
      <Field label={t("onboarding.configure.purpose")} hint={t("onboarding.configure.purposeHint")}>
        <Select value={loading ?? current} disabled={loading !== null} onChange={(event) => { change(event.target.value); }}>
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.key === recommended ? t("onboarding.configure.purposeRecommended", { name: option.name }) : option.name}
            </option>
          ))}
        </Select>
      </Field>
      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <DialogContent
          title={
            <span className="flex items-center gap-2">
              <Icon name="alert-triangle" className="size-5 text-warning" />
              {t(replace === "ai" ? "onboarding.configure.replaceAiTitle" : "onboarding.configure.replaceTitle", { template: pending?.name ?? "" })}
            </span>
          }
          description={t("onboarding.configure.replaceBody")}
          closeLabel={t("common.close")}
          className="max-w-md"
          footer={
            <>
              <Button variant="ghost" onClick={() => { setPending(null); }}>
                {t("onboarding.configure.replaceKeep")}
              </Button>
              <Button onClick={confirm}>{t("onboarding.configure.replaceConfirm")}</Button>
            </>
          }
        />
      </Dialog>
    </>
  );
}
